<script setup>
  import { ref, computed, defineAsyncComponent } from 'vue';
  import { useSpotStore } from '@/stores/SpotStore';
  import { useMediaQuery } from '@/composables/useMediaQuery';

  import CompleteReportDataContainerSection from '@/components/CompleteReportDataContainerSection.vue';
  import UpgradeButton from '@/components/UpgradeButton.vue';
  import DataContainer from '@/components/DataContainer.vue';

  const WindGraphs = defineAsyncComponent(() => import('@/components/WindGraphs.vue')); 

  const props = defineProps({
    upgrade: {
      type: Array,
    required: true
    },
    title: {
      type: String,
      required: true
    },
  });

  const spotStore = useSpotStore();

  const id = 'graph';

  const isDesktop = useMediaQuery('(min-width: 840px)');

  const content = ref(null);
  const graphType = ref('basic');

  const messageText = computed(() => {
    if (props.upgrade[1]?.length === 0)  return `Upgrade to view this station's <strong>wind graph.</strong>`;
    
    return `Upgrade to <strong>${formatMembershipLevels(props.upgrade[1])}</strong> to view this station's <strong>wind graph.</strong>`;
  });

  const secondaryMessageText = computed(() => {    
    return `<strong>Pro</strong> &amp; <strong>Gold</strong> users will have access to our exclusive professional meteorologist <strong>Pro Forecast</strong> model written specifically for this station.`;
  });    

  const buttonText = computed(() => {
    if (props.upgrade[2] === null) return 'Upgrade Now';

    return props.upgrade[2] ? 'Start Free Trial' : 'Upgrade Now';
  });

  const minHeight = computed(() => {
    if (!isDesktop.value) return '554px';

    return graphType.value === 'basic' ? '539px' : '526px';
  });

  // TODO: move to composable?
  function formatMembershipLevels(levels) {
    return levels.map((level, i) => {
      const capitalizedLevel = `${level[0].toUpperCase()}${level.slice(1)}`;

      if (i === 0) return capitalizedLevel;

      if (levels.length > 2) {
        return i < levels.length - 1 
          ? `, ${capitalizedLevel}` 
          : `, or ${capitalizedLevel}`
      };

      return ` or ${capitalizedLevel}`
    }).join('');
  }  

  function handleGraphTypeChange(type) {
    graphType.value = type;
  }
</script>

<template>
    <CompleteReportDataContainerSection
      v-if="props.upgrade[0]"
      :sectionId="id"
      :title="props.title"
      v-model:content="content"
      containerTypeId="Current Conditions"
      :metaActions="false"
    >
      <template #content="{ isVisible }">
        <DataContainer style="overflow: hidden;">
          <div class="jwx-data-container__background-image">
            <div class="jwx-data-container__upgrade-content">
              <div class="jwx-data-container__upgrade-block"></div>
              <div class="jwx-data-container__upgrade-message">
                <p class="jwx-data-container__upgrade-message-text" v-html="messageText"></p>
                <p 
                  v-if="spotStore.proForecastIdIfExists"
                  class="jwx-data-container__upgrade-message-secondary-text"
                  v-html="secondaryMessageText"
                ></p>
                <UpgradeButton :buttonText="buttonText" />
              </div>

              <div class="jwx-data-container__upgrade-preview">
                <img src="../assets/images/upgrade/wind-graph-preview.webp" class="jwx-data-container__upgrade-image" />
              </div>
              <div 
                v-if="spotStore.proForecastIdIfExists"
                class="jwx-data-container__upgrade-callout"
              >
                <div class="jwx-data-container__upgrade-callout-graphic">
                  <img src="../assets/images/upgrade/wind-graph-pro-forecast-preview.webp" class="jwx-data-container__upgrade-callout-image" />
                </div>
                <p class="jwx-data-container__upgrade-callout-text">Pro Forecast Model</p>
              </div>
            </div>

            <img src="../assets/images/upgrade/wind-graph-blurred.jpg"  class="jwx-data-container__upgrade-bg-image" />
          </div>
        </DataContainer>
      </template>
    </CompleteReportDataContainerSection>

    <CompleteReportDataContainerSection
      v-else
      v-model:content="content"
      :sectionId="id"
      :title="title"
      containerTypeId="Current Conditions"
      :minHeight="minHeight"
    >
      <template #content="{ isVisible }">
        <WindGraphs
          @graphType:change="handleGraphTypeChange($event)"
          v-if="isVisible"
          v-model:content="content"
        />
      </template>
    </CompleteReportDataContainerSection>
</template>